import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../../api/API";
import ErrorPage from "../ErrorPage/ErrorPage";
import ActivatingView from "./components/ActivatingView";
import { ErrorViewButtonConfig } from "../../types/types";
import ActivatingEmailInput from "./components/ActivatingEmailInput";

let hasFetched = false;

const ActivatingPage = () => {
  const [confirmationNumber, setConfirmationNumber] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isEmailValidated, setIsEmailValidated] = useState<boolean>(false);

  const [errorEmail, setErrorEmail] = useState<string>();
  const [errorButtonConfig, setErrorButtonConfig] =
    useState<ErrorViewButtonConfig>({ showButton: false });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const activateDataplan = useCallback(async () => {
    // Mark that the effect has run
    hasFetched = true;

    if (!confirmationNumber) {
      setErrorMessage("Missing URL parameters");
      return;
    }

    try {
      const response = await API.post("devices/activation/activateDataPlan", {
        confirmationNumber,
      });

      // status is sometimes boolean and sometimes a number in CC code. This is changed so false is -1 and true is 0.
      const {
        data: { status },
      } = response;

      if (status === 0) {
        const {
          data: { encryptedEmail, encryptedPhone },
        } = response;

        navigate(`/activated?pn=${encryptedPhone}&email=${encryptedEmail}`);
      }
    } catch (error: any) {
      setErrorMessage(error.response.data.message as string);
      console.error(
        "Error calling `devices/activateDataPlan` api using confirmation_number",
        error,
        {
          message: error.response.data.message,
          methodName: "activateDataplan",
          timeStamp: new Date().toISOString(),
        }
      );
      try {
        switch (error.response.data.block) {
          // Show Error View with "try again" button that reloads the page
          case "response_block":
            setErrorButtonConfig({
              showButton: true,
              onClick: () => window.location.reload(),
              text: "Try Again",
            });
            break;
          // Show Error View without the "try again" button
          case "response_block2":
            setErrorButtonConfig({ showButton: false });
            break;
          // Show Email Mismatch View
          case "email_mismatch":
            setErrorMessage("email-mismatch");
            setErrorEmail(error.response.data.email as string);
            break;
          case "activation_pending":
            if (String(error?.response?.data?.message)?.includes("031")) {
              navigate("/in-queue");
            }
            break;
          // Show Error View without the "try again" button
          default:
            setErrorButtonConfig({ showButton: false });
            break;
        }
      } catch (e) {
        setErrorMessage("Unknown Error");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmationNumber, navigate]);

  useEffect(() => {
    setConfirmationNumber(searchParams.get("confirmation_number"));

    // Only proceed if the effect hasn't run yet
    if (!hasFetched && isEmailValidated) {
      activateDataplan();
    }
  }, [activateDataplan, searchParams, isEmailValidated]);

  const emailMismatchUpdateHandler = () => {
    API.post("customers/emailMismatch", {
      confirmationNumber,
    })
      .then(() => {
        // Reset error values
        setErrorMessage(undefined);
        setErrorEmail(undefined);
        setErrorButtonConfig({ showButton: false });
        // Call the /activateDataPlan endpoint again
        activateDataplan();
      })
      .catch(() => {
        // Show Error View with "try again" button that reloads the page
        setErrorButtonConfig({
          showButton: true,
          onClick: () => window.location.reload(),
          text: "Try Again",
        });
        setErrorMessage("Unkown Error");
        console.error(
          "Error calling `customers/emailMismatch` api using confirmation_number",
          {
            methodName: "emailMismatchUpdateHandler",
            timeStamp: new Date().toISOString(),
          }
        );
      });
  };

  return errorMessage ? (
    <ErrorPage
      message={errorMessage}
      email={errorEmail}
      buttonConfig={errorButtonConfig}
      onEmailMismatchUpdate={emailMismatchUpdateHandler}
    />
  ) : isEmailValidated ? (
    <ActivatingView />
  ) : (
    <ActivatingEmailInput confirmationNumber={confirmationNumber} setIsEmailValidated={setIsEmailValidated} setErrorMessage={setErrorMessage} />
  );
};

export default ActivatingPage;
